/**
 * AnalyticsPage - Artist analytics dashboard
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, Users, Play, Music, Calendar, MapPin,
  ArrowLeft, ArrowUp, ArrowDown, Clock, Eye, Heart,
  BarChart3, PieChart
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card
const StatCard = ({ icon: Icon, label, value, change, changeType, color }) => (
  <div className="bg-[#12101A] rounded-2xl p-5 border border-white/5">
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      {change && (
        <span className={`flex items-center gap-1 text-sm ${
          changeType === 'up' ? 'text-green-400' : 'text-red-400'
        }`}>
          {changeType === 'up' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
          {change}%
        </span>
      )}
    </div>
    <p className="text-2xl font-bold text-white mb-1">{value}</p>
    <p className="text-[#71717A] text-sm">{label}</p>
  </div>
);

// Track Performance Row
const TrackRow = ({ track, rank }) => (
  <div className="flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors">
    <span className="text-[#71717A] text-sm font-medium w-6">{rank}</span>
    <div className="w-10 h-10 rounded-lg bg-white/10 overflow-hidden flex-shrink-0">
      {track.artwork ? (
        <img src={track.artwork} alt={track.track_title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Music className="w-4 h-4 text-[#71717A]" />
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium truncate text-sm">{track.track_title}</p>
    </div>
    <div className="text-right">
      <p className="text-white font-medium">{track.play_count?.toLocaleString() || 0}</p>
      <p className="text-[#71717A] text-xs">plays</p>
    </div>
  </div>
);

// Location Item
const LocationItem = ({ location, count, percentage }) => (
  <div className="flex items-center gap-3">
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF007F] to-[#00F0FF] flex items-center justify-center flex-shrink-0">
      <MapPin className="w-4 h-4 text-white" />
    </div>
    <div className="flex-1">
      <p className="text-white text-sm font-medium">{location}</p>
      <div className="h-1.5 bg-white/10 rounded-full mt-1 overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF]"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
    <span className="text-[#71717A] text-sm">{count}</span>
  </div>
);

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  const [analytics, setAnalytics] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!isAuthenticated) return;
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch analytics data
        const [overviewRes, tracksRes, audienceRes] = await Promise.all([
          axios.get(`${API_URL}/api/artists/analytics/overview`, { headers }).catch(() => null),
          axios.get(`${API_URL}/api/artists/radio/analytics/my-tracks`, { headers }).catch(() => null),
          axios.get(`${API_URL}/api/artists/analytics/audience`, { headers }).catch(() => null)
        ]);
        
        setAnalytics({
          overview: overviewRes?.data || {},
          audience: audienceRes?.data || {}
        });
        
        setTracks(tracksRes?.data?.tracks || []);
        
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchAnalytics();
    }
  }, [isAuthenticated, authLoading, timeRange]);

  if (!authLoading && !isAuthenticated) {
    navigate('/auth');
    return null;
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-[#FF007F] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  const overview = analytics?.overview || {};
  const audience = analytics?.audience || {};

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-white/5"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
              Analytics
            </h1>
          </div>
          
          {/* Time Range Selector */}
          <div className="flex gap-2">
            {['7d', '30d', '90d'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-[#FF007F] text-white'
                    : 'bg-white/5 text-[#71717A] hover:bg-white/10'
                }`}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <StatCard
            icon={Play}
            label="Total Plays"
            value={overview.total_plays?.toLocaleString() || '0'}
            change={12}
            changeType="up"
            color="bg-gradient-to-br from-[#FF007F] to-[#FF007F]/50"
          />
          <StatCard
            icon={Users}
            label="Followers"
            value={overview.followers?.toLocaleString() || '0'}
            change={8}
            changeType="up"
            color="bg-gradient-to-br from-[#00F0FF] to-[#00F0FF]/50"
          />
          <StatCard
            icon={Music}
            label="Tracks"
            value={overview.total_tracks?.toString() || '0'}
            color="bg-gradient-to-br from-purple-500 to-purple-500/50"
          />
          <StatCard
            icon={Clock}
            label="Avg. Listen Time"
            value={overview.avg_listen_time || '0:00'}
            color="bg-gradient-to-br from-green-500 to-green-500/50"
          />
        </div>

        {/* Top Tracks */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#FF007F]" />
            Top Performing Tracks
          </h2>
          <div className="bg-[#12101A] rounded-2xl border border-white/5 overflow-hidden">
            {tracks.length > 0 ? (
              tracks.slice(0, 5).map((track, i) => (
                <TrackRow key={track.id} track={track} rank={i + 1} />
              ))
            ) : (
              <div className="p-8 text-center text-[#71717A]">
                <Music className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tracks yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Audience Insights */}
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
            <PieChart className="w-5 h-5 text-[#00F0FF]" />
            Audience Insights
          </h2>
          <div className="bg-[#12101A] rounded-2xl border border-white/5 p-5">
            <h3 className="text-white font-medium mb-4">Top Locations</h3>
            {audience.top_locations?.length > 0 ? (
              <div className="space-y-4">
                {audience.top_locations.slice(0, 5).map((loc, i) => (
                  <LocationItem
                    key={i}
                    location={loc.city || loc.location || 'Unknown'}
                    count={loc.count}
                    percentage={(loc.count / (audience.top_locations[0]?.count || 1)) * 100}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#71717A]">
                <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Location data will appear as you get more plays</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
